import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useNavigate, useParams } from '@ui/app-router';
import { Button } from '@ui/primitives/button';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarInput } from '@ui/primitives/sidebar';
import { Skeleton } from '@ui/primitives/skeleton';
import { cn } from '@ui/primitives/utils';
import { Search, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useGetCurrentUserOrganizationList } from 'ze-sdk';
import { ProjectsList, useExpandedProjects } from './sidebar';
export function AppSidebar(param) {
    let { className } = param;
    const { data: organizationListResp, isLoading: orgsLoading } = useGetCurrentUserOrganizationList();
    const navigate = useNavigate();
    const { organization } = useParams({
        strict: false
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    useEffect(()=>{
        const timer = setTimeout(()=>{
            setDebouncedSearchQuery(searchQuery);
        }, 300);
        return ()=>clearTimeout(timer);
    }, [
        searchQuery
    ]);
    const { expandedProjects, toggleProject, expandProject, expandAll, collapseAll } = useExpandedProjects(organization !== null && organization !== void 0 ? organization : '');
    const organizationList = organizationListResp === null || organizationListResp === void 0 ? void 0 : organizationListResp.entities;
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            const activeElement = document.activeElement;
            const isInputFocused = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'SELECT' || activeElement.getAttribute('contenteditable') === 'true');
            if (!isInputFocused && organizationList && organizationList.length > 1) {
                const currentOrgIndex = organizationList.findIndex((org)=>org.name === organization);
                if (event.key === 'j') {
                    event.preventDefault();
                    const nextIndex = (currentOrgIndex + 1) % organizationList.length;
                    const targetOrg = organizationList[nextIndex];
                    if (!targetOrg) return;
                    navigate({
                        to: "/org/".concat(targetOrg.name)
                    }).catch((error)=>{
                        if (error instanceof Error) {
                            throw new Error("Navigation error: ".concat(error.message));
                        } else {
                            throw new Error('Unknown navigation error');
                        }
                    });
                } else if (event.key === 'k') {
                    event.preventDefault();
                    const prevIndex = currentOrgIndex === 0 ? organizationList.length - 1 : currentOrgIndex - 1;
                    const targetOrg = organizationList[prevIndex];
                    if (!targetOrg) return;
                    navigate({
                        to: "/org/".concat(targetOrg.name)
                    }).catch((error)=>{
                        if (error instanceof Error) {
                            throw new Error("Navigation error: ".concat(error.message));
                        } else {
                            throw new Error('Unknown navigation error');
                        }
                    });
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return ()=>{
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        organization,
        organizationList,
        navigate
    ]);
    if (orgsLoading || !organizationList) {
        return /*#__PURE__*/ _jsxs(Sidebar, {
            collapsible: "offcanvas",
            className: cn(className),
            children: [
                /*#__PURE__*/ _jsx(SidebarHeader, {
                    children: /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-16 w-full"
                    })
                }),
                /*#__PURE__*/ _jsxs(SidebarContent, {
                    children: [
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-6 w-full"
                        }),
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-6 w-full"
                        }),
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-6 w-full"
                        })
                    ]
                })
            ]
        });
    }
    if (!organization) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Sidebar, {
        className: cn(className),
        "data-e2e": "LAYOUT_SIDEBAR",
        children: [
            /*#__PURE__*/ _jsx(SidebarHeader, {
                className: "border-b border-border px-3 py-2",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "relative flex items-center",
                    children: [
                        /*#__PURE__*/ _jsx(Search, {
                            className: "absolute left-2 h-4 w-4 text-muted-foreground"
                        }),
                        /*#__PURE__*/ _jsx(SidebarInput, {
                            placeholder: "Search...",
                            value: searchQuery,
                            onChange: (e)=>setSearchQuery(e.target.value),
                            className: "pl-8 bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-white/25",
                            "data-e2e": "LAYOUT_SIDEBAR__SEARCH_INPUT"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SidebarContent, {
                "data-e2e": "LAYOUT_SIDEBAR__CONTENT",
                children: /*#__PURE__*/ _jsx(ProjectsList, {
                    organization: organization,
                    expandedProjects: expandedProjects,
                    onToggleProject: toggleProject,
                    onExpandProject: expandProject,
                    onExpandAll: expandAll,
                    onCollapseAll: collapseAll,
                    searchQuery: debouncedSearchQuery
                })
            }),
            /*#__PURE__*/ _jsx(SidebarFooter, {
                className: "border-t border-border p-3",
                children: /*#__PURE__*/ _jsx(Button, {
                    asChild: true,
                    variant: "ghost",
                    size: "sm",
                    className: "h-9 px-4 gap-2 bg-white/5 border border-white/15 shadow-xs rounded-lg text-muted-foreground hover:bg-white/20 hover:text-foreground",
                    "data-e2e": "LAYOUT_SIDEBAR__CHANGELOG_LINK",
                    children: /*#__PURE__*/ _jsxs("a", {
                        href: "https://zephyr-cloud.io/changelog",
                        target: "_blank",
                        rel: "noopener noreferrer",
                        children: [
                            /*#__PURE__*/ _jsx(Sparkles, {
                                className: "h-4 w-4 flex-shrink-0 text-primary"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: "Discover what's new!"
                            })
                        ]
                    })
                })
            })
        ]
    });
}
